/**
 * PR-α3b U5: harvest-driven `Lot.stage` auto-transition thresholds.
 *
 * Cumulative harvest kg for a lot (all `HarvestLog.harvestKg`, including the
 * one just saved) is compared against the lot's live, growth-aware current
 * biomass (`computeCurrentBiomassPerLot`, T3-B-06e). `Lot` has no
 * `stockedCount` / `biomassAtStocking` field to compare against instead —
 * see docs/lot-lifecycle-vs-concerns-audit.md §1.1 X8/X9.
 *
 * Hardcoded constants, no settings/env override — precedent:
 * `LIVE_PREVIEW_STALE_SOURCE_THRESHOLD_MS` in
 * packages/api/src/services/LotStoryService.ts. Changing these thresholds
 * is a code change + PR, not a runtime preference.
 */

export const HARVEST_TOTAL_WARNING_THRESHOLD = 0.80
export const HARVEST_TOTAL_CONFIRM_THRESHOLD = 0.95

export type HarvestTransitionSignal = 'below' | 'warning' | 'total'

/**
 * Pure, side-effect-free. `currentBiomassKg` may be `null`/`undefined` (e.g.
 * a lot with no stocking log) or `<= 0`; both fall back to `'below'` rather
 * than throwing or dividing by zero.
 */
export const evaluateHarvestTransitionSignal = (
  cumulativeHarvestKg: number,
  currentBiomassKg: number | null | undefined
): HarvestTransitionSignal => {
  if (currentBiomassKg == null || currentBiomassKg <= 0) return 'below'

  const ratio = cumulativeHarvestKg / currentBiomassKg
  if (ratio >= HARVEST_TOTAL_CONFIRM_THRESHOLD) return 'total'
  if (ratio >= HARVEST_TOTAL_WARNING_THRESHOLD) return 'warning'
  return 'below'
}
